import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICreateGrouping, IGrouping, PalletisingService, TGroupingTypes } from '@shared/services/palletising.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-grouping-dialog',
	templateUrl: './create-update-grouping.html',
	styleUrls: ['./create-update-grouping.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateGroupingDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	public form: FormGroup;
	public httpError: string;
	public loading: boolean;
	public types: TGroupingTypes;
	public grouping: IGrouping | ICreateGrouping;
	public isCreatingGrouping: boolean;

	public constructor(
		@Inject(MAT_DIALOG_DATA) public editGrouping: IGrouping | null,
		public dialogRef: MatDialogRef<CreateUpdateGroupingDialogComponent>,
		private palletisingService: PalletisingService,
		private fb: FormBuilder,
	) {
		this.httpError = '';
		this.loading = false;
		this.types = ['Type', 'Dimension'];
		this.grouping = editGrouping ? { ...editGrouping } : this.defaultGrouping;
		this.isCreatingGrouping = editGrouping === null;
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			name: [this.grouping.name, [Validators.required, Validators.maxLength(20), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			description: [this.grouping.description, [Validators.maxLength(100), Validators.pattern(ValidatorPatterns.DanishCharacters)]],
			type: [this.grouping.type, [Validators.required]],
		});
		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value: IGrouping | ICreateGrouping) => {
			this.grouping.description = value.description ? value.description : null;
			this.grouping.name = value.name ? value.name : null;
			this.grouping.type = value.type;
		});
	}

	public get controls() {
		return this.form.controls;
	}

	private get defaultGrouping(): ICreateGrouping {
		return {
			description: null,
			name: null,
			type: 'Type',
		};
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public async onSave(): Promise<void> {
		this.httpError = '';
		if (this.form.invalid) return;

		if (this.loading) return;
		this.loading = true;

		this.getSaveObservable().subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.loading = false;
			},
		});
	}

	private getSaveObservable() {
		if ('self' in this.grouping) {
			return this.palletisingService.updateGrouping(this.grouping);
		}
		return this.palletisingService.createGrouping(this.grouping);
	}
}
